import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

const TeamCtx = createContext({
  teams: [],
  activeTeamId: null,
  activeTeam: null,
  loading: false,
  refresh: async () => {},
  createTeam: async () => {},
  renameTeam: async () => {},
  setCategory: async () => {},
  deleteTeam: async () => {},
  switchTeam: async () => {},
});

export function TeamProvider({ children }) {
  const { user } = useAuth();
  const [teams, setTeams] = useState([]);
  const [activeTeamId, setActiveTeamId] = useState(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!user || user.role === 'player') return;
    setLoading(true);
    try {
      const { data } = await api.get('/teams');
      setTeams(data.teams || []);
      setActiveTeamId(data.active_team_id || (data.teams?.[0]?.id ?? null));
    } catch (e) {
      // network or 403; leave state empty
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { refresh(); }, [refresh]);

  const createTeam = useCallback(async (name, category = 'mixed') => {
    const { data } = await api.post('/teams', { name, category });
    await refresh();
    return data;
  }, [refresh]);

  const renameTeam = useCallback(async (id, name) => {
    await api.patch(`/teams/${id}`, { name });
    await refresh();
  }, [refresh]);

  const setCategory = useCallback(async (id, category) => {
    await api.patch(`/teams/${id}`, { category });
    await refresh();
  }, [refresh]);

  const deleteTeam = useCallback(async (id) => {
    await api.delete(`/teams/${id}`);
    await refresh();
  }, [refresh]);

  const switchTeam = useCallback(async (id) => {
    setActiveTeamId(id); // optimistic
    try {
      await api.post(`/teams/${id}/activate`);
    } catch (e) {
      await refresh();
    }
  }, [refresh]);

  const activeTeam = useMemo(
    () => teams.find(t => t.id === activeTeamId) || null,
    [teams, activeTeamId]
  );

  const value = useMemo(() => ({
    teams, activeTeamId, activeTeam, loading,
    refresh, createTeam, renameTeam, setCategory, deleteTeam, switchTeam,
  }), [teams, activeTeamId, activeTeam, loading, refresh, createTeam, renameTeam, setCategory, deleteTeam, switchTeam]);

  return <TeamCtx.Provider value={value}>{children}</TeamCtx.Provider>;
}

export const useTeams = () => useContext(TeamCtx);
